import { Button, FormComboBox } from '@civilgrid/ui/components';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { editProject as apiEditProject, getProject as apiGetProject } from '~/api/projects2';
import authorizations, { Action } from '~/authorizations';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Checkbox from '~/components2/forms/Checkbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import Icon from '~/components2/Icon';
import { Notify } from '~/components2/Notify';
import { placeholderAndTooltips } from '~/pages/common/NewProject/common';
import { type ProjectWithDetails as Project, projectTypeOptions } from '~/pages/projectDetails/details/common';
import Section from '~/pages/projectDetails/details/Section';
import rootStore from '~/store';
import store from '../../../../store';
import { DrawerContainer, DrawerContent, DrawerFooter } from '../../../Drawer';
import { NextButton, PreviousButton } from '../../StepButtons';
import {
  type ConfidentialWarningAction,
  type ConfidentialWarningState,
  confidentialWarningReducer,
} from './confidentialWarning';

type TFormValues = {
  client?: string;
  type?: string;
  scope?: string;
  projectVicinity?: string;
  peName?: string;
  peLicenseNumber?: string;
  peLicenseState?: string;
  isConfidential?: boolean;
  address2: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    crossStreets: string;
  };
};

export default function NarrativeComponent() {
  const [isProcessing, setIsProcessing] = useState(false);
  const { project, formState } = useSnapshot(store) as typeof store;
  const unAuthorized = !authorizations.can(Action.EditProjectDetails);

  const form = useForm<TFormValues>({
    defaultValues: {
      client: project?.client || '',
      type: project?.type || '',
      scope: project?.scope || '',
      projectVicinity: project?.projectVicinity || '',
      peName: project?.peName || '',
      peLicenseNumber: project?.peLicenseNumber || '',
      peLicenseState: project?.peLicenseState || '',
      isConfidential: project?.isConfidential || false,
      address2: project?.address2 || {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        crossStreets: '',
      },
    },
  });

  // Confidential-project warning interaction. `isConfidential` is mirrored back into
  // the form so it still flows through dirty-tracking and the save payload.
  const [confidentialWarning, setConfidentialWarning] = useState<ConfidentialWarningState>({
    isConfidential: project?.isConfidential ?? false,
    showWarning: false,
  });

  const confidentialAnalyticsProps = () => ({
    project_id: project?.id ?? '',
    organization_id: project?.organization,
  });

  const dispatchConfidential = (action: ConfidentialWarningAction): ConfidentialWarningState => {
    const next = confidentialWarningReducer(confidentialWarning, action);
    setConfidentialWarning(next);
    form.setValue('isConfidential', next.isConfidential, { shouldDirty: true, shouldValidate: true });
    return next;
  };

  const handleConfidentialToggle = (checked: boolean) => {
    const wasWarningOpen = confidentialWarning.showWarning;
    dispatchConfidential({ type: 'toggle', checked });

    if (checked) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectConfidentialWarningShown, confidentialAnalyticsProps());
    } else if (wasWarningOpen) {
      // Unchecking while the banner is open counts as backing out of the flag.
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectConfidentialWarningResolved, {
        ...confidentialAnalyticsProps(),
        decision: 'removed',
      });
    }
  };

  const handleConfidentialConfirm = () => {
    dispatchConfidential({ type: 'confirm' });
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectConfidentialWarningResolved, {
      ...confidentialAnalyticsProps(),
      decision: 'confirmed',
    });
  };

  const handleConfidentialRemove = () => {
    dispatchConfidential({ type: 'removeFlag' });
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectConfidentialWarningResolved, {
      ...confidentialAnalyticsProps(),
      decision: 'removed',
    });
  };

  // see https://github.com/react-hook-form/react-hook-form/issues/4740
  const isDirty = !isEmpty(form.formState.dirtyFields);

  // toggle confirmed to false if form is dirty, want user to save
  // info before proceeding.
  useEffect(() => {
    if (isDirty) {
      store.formState.narrativeConfirmed = false;
    }
  }, [isDirty]);

  const onSubmit: SubmitHandler<TFormValues> = async (data: { [key: string]: any }) => {
    if (!isDirty || !form.formState.isValid || isEmpty(form.formState.dirtyFields)) return;

    setIsProcessing(true);

    const edits: { attributes: { [key: string]: any } } = {
      attributes: {},
    };

    for (const key in form.formState.dirtyFields) {
      edits.attributes[key] = data[key];
    }

    try {
      await apiEditProject(project!.id, edits);
      const p = await apiGetProject<Project>(project!.id);
      store.project = p;
      store.formState.narrativeConfirmed = true;

      form.reset(undefined, { keepValues: true });

      capturePosthogEvent(POSTHOG_EVENTS.projectDetailUpdated, {
        project_id: p.id,
        organization_id: p.organization,
        edited_in_data_request_form: true,
        updated_fields: Object.keys(edits.attributes ?? {}),
      });

      Notify.info({
        title: 'Update Project Narrative',
        message: 'Project narrative successfully updated.',
      });
    } catch {
      Notify.error({
        title: 'Update Project Narrative',
        message: 'An error occurred updating the project narrative, please contact support.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const isConfirmationVisible = !isDirty && form.formState.isValid;

  const isNextButtonDisabled =
    !form.formState.isValid ||
    isDirty ||
    isProcessing ||
    !formState.narrativeConfirmed ||
    confidentialWarning.showWarning;

  return (
    <DrawerContainer>
      <DrawerContent>
        <div className="mb-4">A detailed and accurate project narrative helps with successful data requests.</div>

        <UnAuthorizedBadge unAuthorized={unAuthorized} />

        <Form onSubmit={form.handleSubmit(onSubmit)}>
          <Section title="Project Narrative">
            <div className="grid grid-cols-2 gap-2 gap-x-4">
              <ControlledInput
                name="client"
                label="Client"
                control={form.control}
                placeholder="City of ABC"
                disabled={unAuthorized}
                isRequired
              />

              <FormComboBox
                name="type"
                label="Project Category"
                control={form.control}
                options={projectTypeOptions}
                disabled={unAuthorized}
              />

              <div className="col-span-2">
                <ControlledTextArea
                  name="scope"
                  label="Scope"
                  control={form.control}
                  placeholder={placeholderAndTooltips.scopePlaceholder}
                  tooltipText={placeholderAndTooltips.scopeTooltip}
                  disabled={unAuthorized}
                  isRequired
                />
              </div>

              <div className="col-span-2">
                <ControlledTextArea
                  name="projectVicinity"
                  label="Project Vicinity"
                  control={form.control}
                  tooltipText={placeholderAndTooltips.projectVicinityTooltip}
                  placeholder={placeholderAndTooltips.projectVicinityPlaceholder}
                  disabled={unAuthorized}
                  isRequired
                />
              </div>

              <div className="col-span-2">
                <ControlledInput name="peName" label="PE/PG/PLS Name" control={form.control} disabled={unAuthorized} />
              </div>

              <ControlledInput
                name="peLicenseNumber"
                label="PE/PG/PLS License Number"
                control={form.control}
                disabled={unAuthorized}
              />

              <ControlledInput
                name="peLicenseState"
                label="PE/PG/PLS License State"
                control={form.control}
                disabled={unAuthorized}
              />
            </div>
          </Section>

          <Section title="Confidential Project">
            <p className="mb-2 text-xs text-[#333C41]">
              Mark this project as confidential, which will not share the scope or client information with agencies
              you're requesting data from.
            </p>

            <ConfidentialAttentionBanner />

            <div className="mt-2">
              <Checkbox
                label={
                  <span className="normal-case">
                    This project is confidential. Do not share the scope or client information with agencies.
                  </span>
                }
                checked={confidentialWarning.isConfidential}
                value={confidentialWarning.isConfidential}
                disabled={unAuthorized}
                onChange={handleConfidentialToggle}
                data-testid="confidential-project-checkbox"
              />
              {confidentialWarning.showWarning && (
                <ConfidentialConfirmPrompt onConfirm={handleConfidentialConfirm} onRemove={handleConfidentialRemove} />
              )}
            </div>
          </Section>

          <Section title="Project Location">
            <div className="grid grid-cols-2 gap-2 gap-x-4">
              <div className="col-span-2">
                <ControlledInput
                  control={form.control}
                  label="Address"
                  name="address2.street"
                  isRequired
                  tooltipText={placeholderAndTooltips.addressTooltip}
                />
              </div>

              <div className="col-span-2">
                <ControlledInput control={form.control} label="Cross Streets" name="address2.crossStreets" isRequired />
              </div>

              <div className="col-span-3">
                <div className="grid grid-cols-3 gap-x-4">
                  <ControlledInput control={form.control} label="City" name="address2.city" isRequired />
                  <ControlledInput control={form.control} label="State" name="address2.state" isRequired />
                  <ControlledInput control={form.control} label="ZIP Code" name="address2.zipCode" isRequired />
                </div>
              </div>
            </div>
          </Section>

          {/* only show for authorized editors */}
          {!unAuthorized && isDirty && (
            <div className="my-4">
              <Button
                data-testid="request-data-narrative-save"
                type="submit"
                loading={isProcessing}
                disabled={!isDirty || isProcessing}
              >
                Save Project Narrative
              </Button>
              <div className="my-1 text-xs">
                Note: Any changes to the Project Narrative will also be reflected on the Project Info tab.
              </div>
            </div>
          )}
        </Form>

        {isConfirmationVisible && (
          <div className="mt-2">
            <Checkbox
              data-testid="request-data-narrative-confirmed"
              isRequired
              label={'Confirm Project Narrative & Location'}
              checked={formState.narrativeConfirmed}
              value={formState.narrativeConfirmed}
              disabled={isDirty || isProcessing}
              onChange={(checked) => {
                store.formState.narrativeConfirmed = checked;
              }}
            />
          </div>
        )}
      </DrawerContent>
      <DrawerFooter>
        <PreviousButton disabled={isDirty} onClick={() => store.toggleFormStep(-1)} />

        <NextButton disabled={isNextButtonDisabled} onClick={() => store.toggleFormStep(1)} />
      </DrawerFooter>
    </DrawerContainer>
  );
}

const ConfidentialAttentionBanner = () => (
  <p className="text-xs leading-snug text-[#333C41]" data-testid="confidential-attention-banner">
    <span className="font-bold">Attention:</span> Marking a project as confidential typically adds 5–10 business days to
    a records research request. Some agencies may refuse to provide information entirely for confidential projects.
  </p>
);

// Confirm gate shown after the user checks the box; blocks Next Step until resolved.
const ConfidentialConfirmPrompt = ({ onConfirm, onRemove }: { onConfirm: () => void; onRemove: () => void }) => (
  <div
    className="mt-2 rounded-md border-l-4 border-[#FFC700] bg-[#FFF8E6] p-3 text-xs text-[#333C41]"
    data-testid="confidential-confirm-prompt"
    role="alert"
  >
    <div className="flex items-center gap-2 font-bold text-[#8A6D00]">
      <Icon icon="warning" size={16} />
      <span>Before you continue</span>
    </div>
    <p className="mt-1">Are you sure this project requires confidential handling?</p>
    <div className="mt-3 flex flex-wrap gap-2">
      <Button onClick={onConfirm} data-testid="confidential-warning-confirm">
        Yes, this must be confidential
      </Button>
      <Button variant="secondary" onClick={onRemove} data-testid="confidential-warning-remove">
        Remove confidential flag
      </Button>
    </div>
  </div>
);

const UnAuthorizedBadge = ({ unAuthorized }: { unAuthorized: boolean }) => {
  const { myRole } = useSnapshot(rootStore);

  if (!unAuthorized) return null;

  return (
    <div className="my-4 rounded-xl border border-[#FFC700] p-2 text-xs text-[#333C41]">
      ⚠️ Your current role {myRole?.name && `(${myRole.name})`} can confirm the <strong>Project Narrative</strong> but is
      not authorized to edit project info. Please contact your account admin.
    </div>
  );
};
